import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Package, Calendar, ShoppingBag, Plus, Edit, Trash2, Star, Users, MessageSquare, ChevronLeft, ChevronRight, Mail, Eye, Archive, Reply } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useAuth, API } from '@/App';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [users, setUsers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [calendarData, setCalendarData] = useState({ bookings: {} });
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth() + 1);
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    price: '',
    image_url: '',
    type: 'product',
    stock_quantity: ''
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('session_token');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  useEffect(() => {
    if (!user?.is_admin) {
      toast.error('Access denied');
      navigate('/');
      return;
    }
    fetchData();
  }, [user, navigate]);

  useEffect(() => {
    if (activeTab === 'calendar') {
      fetchCalendarData();
    }
  }, [activeTab, calendarMonth, calendarYear]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();

      const [productsRes, ordersRes, bookingsRes, categoriesRes, reviewsRes, usersRes, messagesRes] = await Promise.all([
        fetch(`${API}/products`, { headers, credentials: 'include' }),
        fetch(`${API}/admin/orders`, { headers, credentials: 'include' }),
        fetch(`${API}/admin/bookings`, { headers, credentials: 'include' }),
        fetch(`${API}/categories`, { headers, credentials: 'include' }),
        fetch(`${API}/admin/reviews`, { headers, credentials: 'include' }),
        fetch(`${API}/admin/users`, { headers, credentials: 'include' }),
        fetch(`${API}/admin/messages`, { headers, credentials: 'include' })
      ]);

      if (productsRes.ok) setProducts(await productsRes.json());
      if (ordersRes.ok) setOrders(await ordersRes.json());
      if (bookingsRes.ok) setBookings(await bookingsRes.json());
      if (categoriesRes.ok) setCategories(await categoriesRes.json());
      if (reviewsRes.ok) setReviews(await reviewsRes.json());
      if (usersRes.ok) setUsers(await usersRes.json());
      if (messagesRes.ok) setMessages(await messagesRes.json());
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCalendarData = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API}/admin/bookings/calendar?month=${calendarMonth}&year=${calendarYear}`, { headers, credentials: 'include' });
      if (res.ok) setCalendarData(await res.json());
    } catch (error) {
      console.error('Failed to fetch calendar data:', error);
    }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/admin/products`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          price: parseFloat(formData.price),
          stock_quantity: formData.stock_quantity ? parseInt(formData.stock_quantity) : null
        })
      });

      if (response.ok) {
        toast.success('Product created successfully');
        setIsCreateDialogOpen(false);
        setFormData({ name: '', description: '', category: '', price: '', image_url: '', type: 'product', stock_quantity: '' });
        fetchData();
      } else {
        throw new Error('Failed to create product');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    try {
      const updateData = { ...formData };
      if (updateData.price) updateData.price = parseFloat(updateData.price);
      if (updateData.stock_quantity) updateData.stock_quantity = parseInt(updateData.stock_quantity);

      const response = await fetch(`${API}/admin/products/${editingProduct.product_id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        toast.success('Product updated successfully');
        setIsEditDialogOpen(false);
        setEditingProduct(null);
        setFormData({ name: '', description: '', category: '', price: '', image_url: '', type: 'product', stock_quantity: '' });
        fetchData();
      } else {
        throw new Error('Failed to update product');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to deactivate this product?')) return;

    try {
      const response = await fetch(`${API}/admin/products/${productId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Product deactivated');
        fetchData();
      } else {
        throw new Error('Failed to delete product');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;

    try {
      const response = await fetch(`${API}/admin/reviews/${reviewId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Review deleted');
        fetchData();
      } else {
        throw new Error('Failed to delete review');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    try {
      const response = await fetch(`${API}/admin/orders/${orderId}/status?status=${newStatus}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Order status updated');
        fetchData();
      } else {
        throw new Error('Failed to update order status');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleUpdateBookingStatus = async (bookingId, newStatus) => {
    try {
      const response = await fetch(`${API}/admin/bookings/${bookingId}/status?status=${newStatus}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Booking status updated');
        fetchData();
      } else {
        throw new Error('Failed to update booking status');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleToggleAdmin = async (userId, currentIsAdmin) => {
    if (!window.confirm(`Are you sure you want to ${currentIsAdmin ? 'remove admin rights from' : 'make'} this user ${currentIsAdmin ? '' : 'an admin'}?`)) return;

    try {
      const response = await fetch(`${API}/admin/users/${userId}/role?is_admin=${!currentIsAdmin}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('User role updated');
        fetchData();
      } else {
        throw new Error('Failed to update user role');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleUpdateMessageStatus = async (messageId, newStatus) => {
    try {
      const response = await fetch(`${API}/admin/messages/${messageId}/status?status=${newStatus}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Message status updated');
        fetchData();
      } else {
        throw new Error('Failed to update message status');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;

    try {
      const response = await fetch(`${API}/admin/messages/${messageId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Message deleted');
        fetchData();
      } else {
        throw new Error('Failed to delete message');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      category: product.category,
      price: product.price.toString(),
      image_url: product.image_url,
      type: product.type,
      stock_quantity: product.stock_quantity?.toString() || ''
    });
    setIsEditDialogOpen(true);
  };

  // Calendar helpers
  const getDaysInMonth = (month, year) => new Date(year, month, 0).getDate();
  const getFirstDayOfMonth = (month, year) => new Date(year, month - 1, 1).getDay();
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(calendarMonth, calendarYear);
    const firstDay = getFirstDayOfMonth(calendarMonth, calendarYear);
    const days = [];

    // Empty cells for days before the first day of month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-24 bg-background-elevated/30 rounded-lg"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${calendarYear}-${String(calendarMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayBookings = calendarData.bookings[dateStr] || [];
      const isToday = new Date().toISOString().split('T')[0] === dateStr;

      days.push(
        <div key={day} className={`h-24 bg-background-paper rounded-lg p-2 border ${isToday ? 'border-primary' : 'border-primary/10'} overflow-hidden`}>
          <div className={`text-sm font-medium mb-1 ${isToday ? 'text-primary' : 'text-white'}`}>{day}</div>
          <div className="space-y-1 overflow-y-auto max-h-16">
            {dayBookings.map((booking, idx) => (
              <div key={idx} className={`text-xs px-1.5 py-0.5 rounded truncate ${
                booking.status === 'confirmed' ? 'bg-status-success/20 text-status-success' :
                booking.status === 'completed' ? 'bg-primary/20 text-primary' :
                'bg-status-warning/20 text-status-warning'
              }`}>
                {booking.time_slot} - {booking.experience_name}
              </div>
            ))}
          </div>
        </div>
      );
    }

    return days;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background pt-16">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text-secondary font-body">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-16" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="mb-8">
            <h1 className="text-4xl font-heading font-bold text-white mb-2" data-testid="admin-title">Admin Dashboard</h1>
            <p className="text-text-secondary font-body">Manage products, orders, bookings, reviews, and users</p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
            <div className="bg-background-paper rounded-2xl p-4 border border-primary/20" data-testid="stat-products">
              <div className="flex items-center">
                <div className="p-2 bg-primary/10 rounded-xl mr-3">
                  <Package className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-text-muted font-body text-xs">Products</p>
                  <p className="text-2xl font-heading font-bold text-white">{products.length}</p>
                </div>
              </div>
            </div>
            <div className="bg-background-paper rounded-2xl p-4 border border-primary/20" data-testid="stat-orders">
              <div className="flex items-center">
                <div className="p-2 bg-primary/10 rounded-xl mr-3">
                  <ShoppingBag className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-text-muted font-body text-xs">Orders</p>
                  <p className="text-2xl font-heading font-bold text-white">{orders.length}</p>
                </div>
              </div>
            </div>
            <div className="bg-background-paper rounded-2xl p-4 border border-primary/20" data-testid="stat-bookings">
              <div className="flex items-center">
                <div className="p-2 bg-primary/10 rounded-xl mr-3">
                  <Calendar className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-text-muted font-body text-xs">Bookings</p>
                  <p className="text-2xl font-heading font-bold text-white">{bookings.length}</p>
                </div>
              </div>
            </div>
            <div className="bg-background-paper rounded-2xl p-4 border border-primary/20" data-testid="stat-messages">
              <div className="flex items-center">
                <div className="p-2 bg-primary/10 rounded-xl mr-3">
                  <Mail className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-text-muted font-body text-xs">Messages</p>
                  <p className="text-2xl font-heading font-bold text-white">{messages.filter(m => m.status === 'new').length}</p>
                </div>
              </div>
            </div>
            <div className="bg-background-paper rounded-2xl p-4 border border-primary/20" data-testid="stat-reviews">
              <div className="flex items-center">
                <div className="p-2 bg-primary/10 rounded-xl mr-3">
                  <MessageSquare className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-text-muted font-body text-xs">Reviews</p>
                  <p className="text-2xl font-heading font-bold text-white">{reviews.length}</p>
                </div>
              </div>
            </div>
            <div className="bg-background-paper rounded-2xl p-4 border border-primary/20" data-testid="stat-users">
              <div className="flex items-center">
                <div className="p-2 bg-primary/10 rounded-xl mr-3">
                  <Users className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-text-muted font-body text-xs">Users</p>
                  <p className="text-2xl font-heading font-bold text-white">{users.length}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="mb-6 overflow-x-auto">
            <div className="flex border-b border-white/10 min-w-max">
              {['products', 'orders', 'bookings', 'calendar', 'messages', 'reviews', 'users'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-3 font-heading font-semibold transition-colors capitalize whitespace-nowrap ${activeTab === tab ? 'text-primary border-b-2 border-primary' : 'text-text-muted hover:text-white'}`}
                  data-testid={`tab-${tab}`}
                >
                  {tab === 'messages' && messages.filter(m => m.status === 'new').length > 0 && (
                    <span className="inline-flex items-center justify-center w-5 h-5 mr-1 text-xs font-bold text-white bg-primary rounded-full">
                      {messages.filter(m => m.status === 'new').length}
                    </span>
                  )}
                  {tab}
                </button>
              ))}
            </div>
          </div>

          {/* Products Tab */}
          {activeTab === 'products' && (
            <div data-testid="products-section">
              <div className="mb-6 flex justify-end">
                <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-primary hover:bg-primary-hover text-white rounded-full px-6" data-testid="add-product-btn">
                      <Plus className="w-5 h-5 mr-2" />
                      Add Product
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-background-paper border-primary/20">
                    <DialogHeader>
                      <DialogTitle className="text-2xl font-heading text-primary">Create New Product</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateProduct} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-white mb-2">Name</label>
                        <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required className="bg-background-elevated border-primary/20 text-white" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-white mb-2">Description</label>
                        <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} required rows={3} className="bg-background-elevated border-primary/20 text-white" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-white mb-2">Category</label>
                          <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})} required>
                            <SelectTrigger className="bg-background-elevated border-primary/20 text-white">
                              <SelectValue placeholder="Select category" />
                            </SelectTrigger>
                            <SelectContent className="bg-background-paper border-primary/20">
                              {categories.map(cat => (
                                <SelectItem key={cat.category_id} value={cat.category_id} className="text-white">{cat.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-white mb-2">Type</label>
                          <Select value={formData.type} onValueChange={(value) => setFormData({...formData, type: value})} required>
                            <SelectTrigger className="bg-background-elevated border-primary/20 text-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-background-paper border-primary/20">
                              <SelectItem value="product" className="text-white">Product</SelectItem>
                              <SelectItem value="experience" className="text-white">Experience</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-white mb-2">Price (£)</label>
                          <Input type="number" step="0.01" value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} required className="bg-background-elevated border-primary/20 text-white" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-white mb-2">Stock Quantity</label>
                          <Input type="number" value={formData.stock_quantity} onChange={(e) => setFormData({...formData, stock_quantity: e.target.value})} placeholder="Leave empty for experiences" className="bg-background-elevated border-primary/20 text-white placeholder:text-text-muted" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-white mb-2">Image URL</label>
                        <Input value={formData.image_url} onChange={(e) => setFormData({...formData, image_url: e.target.value})} required className="bg-background-elevated border-primary/20 text-white" />
                      </div>
                      <div className="flex justify-end gap-3">
                        <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)} className="border-white/20 text-white hover:bg-white/5">Cancel</Button>
                        <Button type="submit" className="bg-primary hover:bg-primary-hover text-white">Create Product</Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="grid grid-cols-1 gap-4">
                {products.map(product => (
                  <div key={product.product_id} className="bg-background-paper rounded-2xl p-6 border border-primary/20" data-testid={`product-${product.product_id}`}>
                    <div className="flex gap-6">
                      <img src={product.image_url} alt={product.name} className="w-32 h-32 object-cover rounded-xl" />
                      <div className="flex-1">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h3 className="text-xl font-heading font-semibold text-white">{product.name}</h3>
                            <p className="text-sm text-text-muted">{product.type} • {product.category}</p>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => openEditDialog(product)} className="border-primary/50 text-primary hover:bg-primary/10">
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleDeleteProduct(product.product_id)} className="border-status-error/50 text-status-error hover:bg-status-error/10">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <p className="text-text-secondary text-sm mb-2 line-clamp-2">{product.description}</p>
                        <div className="flex items-center gap-4">
                          <span className="text-xl font-accent text-primary">£{product.price.toFixed(2)}</span>
                          {product.stock_quantity !== null && (
                            <span className="text-sm text-text-muted">Stock: {product.stock_quantity}</span>
                          )}
                          <span className={`px-2 py-1 rounded-full text-xs ${product.is_active ? 'bg-status-success/20 text-status-success' : 'bg-white/10 text-text-muted'}`}>
                            {product.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-background-paper border-primary/20">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-heading text-primary">Edit Product</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleUpdateProduct} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-white mb-2">Name</label>
                      <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="bg-background-elevated border-primary/20 text-white" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-white mb-2">Description</label>
                      <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={3} className="bg-background-elevated border-primary/20 text-white" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-white mb-2">Price (£)</label>
                        <Input type="number" step="0.01" value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} className="bg-background-elevated border-primary/20 text-white" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-white mb-2">Stock Quantity</label>
                        <Input type="number" value={formData.stock_quantity} onChange={(e) => setFormData({...formData, stock_quantity: e.target.value})} className="bg-background-elevated border-primary/20 text-white" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-white mb-2">Image URL</label>
                      <Input value={formData.image_url} onChange={(e) => setFormData({...formData, image_url: e.target.value})} className="bg-background-elevated border-primary/20 text-white" />
                    </div>
                    <div className="flex justify-end gap-3">
                      <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)} className="border-white/20 text-white hover:bg-white/5">Cancel</Button>
                      <Button type="submit" className="bg-primary hover:bg-primary-hover text-white">Update Product</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          )}

          {/* Orders Tab */}
          {activeTab === 'orders' && (
            <div className="space-y-4" data-testid="orders-section">
              {orders.length === 0 ? (
                <div className="text-center py-12 bg-background-paper rounded-2xl border border-primary/20">
                  <ShoppingBag className="w-16 h-16 text-text-muted mx-auto mb-4" />
                  <p className="text-text-secondary font-body">No orders yet</p>
                </div>
              ) : (
                orders.map(order => (
                  <div key={order.order_id} className="bg-background-paper rounded-2xl p-6 border border-primary/20" data-testid={`order-${order.order_id}`}>
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-heading font-semibold text-white">Order #{order.order_id}</h3>
                        <p className="text-sm text-text-muted">{new Date(order.created_at).toLocaleString()}</p>
                        <p className="text-sm text-text-secondary mt-1">User ID: {order.user_id}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <Select value={order.status} onValueChange={(value) => handleUpdateOrderStatus(order.order_id, value)}>
                          <SelectTrigger className={`w-36 rounded-full text-sm ${
                            order.status === 'delivered' ? 'bg-status-success/20 text-status-success border-status-success/50' :
                            order.status === 'shipped' ? 'bg-blue-500/20 text-blue-400 border-blue-400/50' :
                            order.status === 'processing' ? 'bg-purple-500/20 text-purple-400 border-purple-400/50' :
                            order.status === 'paid' ? 'bg-status-success/20 text-status-success border-status-success/50' :
                            order.status === 'cancelled' ? 'bg-status-error/20 text-status-error border-status-error/50' :
                            'bg-status-warning/20 text-status-warning border-status-warning/50'
                          }`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-background-paper border-primary/20">
                            <SelectItem value="pending" className="text-status-warning">Pending</SelectItem>
                            <SelectItem value="paid" className="text-status-success">Paid</SelectItem>
                            <SelectItem value="processing" className="text-purple-400">Processing</SelectItem>
                            <SelectItem value="shipped" className="text-blue-400">Shipped</SelectItem>
                            <SelectItem value="delivered" className="text-status-success">Delivered</SelectItem>
                            <SelectItem value="cancelled" className="text-status-error">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2 mb-4">
                      {order.items.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span className="text-text-secondary">{item.product_name} x{item.quantity}</span>
                          <span className="text-white font-medium">£{(item.price * item.quantity).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="border-t border-white/10 pt-4">
                      <div className="flex justify-between text-lg font-heading font-bold">
                        <span className="text-white">Total</span>
                        <span className="text-primary">£{order.total_amount.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Bookings Tab */}
          {activeTab === 'bookings' && (
            <div className="space-y-4" data-testid="bookings-section">
              {bookings.length === 0 ? (
                <div className="text-center py-12 bg-background-paper rounded-2xl border border-primary/20">
                  <Calendar className="w-16 h-16 text-text-muted mx-auto mb-4" />
                  <p className="text-text-secondary font-body">No bookings yet</p>
                </div>
              ) : (
                bookings.map(booking => (
                  <div key={booking.booking_id} className="bg-background-paper rounded-2xl p-6 border border-primary/20" data-testid={`booking-${booking.booking_id}`}>
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-heading font-semibold text-white">{booking.experience_name}</h3>
                        <p className="text-sm text-text-muted">Booking #{booking.booking_id}</p>
                        <p className="text-sm text-text-secondary mt-1">User ID: {booking.user_id}</p>
                      </div>
                      <Select value={booking.status} onValueChange={(value) => handleUpdateBookingStatus(booking.booking_id, value)}>
                        <SelectTrigger className={`w-36 rounded-full text-sm ${
                          booking.status === 'confirmed' ? 'bg-status-success/20 text-status-success border-status-success/50' :
                          booking.status === 'completed' ? 'bg-primary/20 text-primary border-primary/50' :
                          booking.status === 'cancelled' ? 'bg-status-error/20 text-status-error border-status-error/50' :
                          'bg-status-warning/20 text-status-warning border-status-warning/50'
                        }`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-background-paper border-primary/20">
                          <SelectItem value="pending" className="text-status-warning">Pending</SelectItem>
                          <SelectItem value="confirmed" className="text-status-success">Confirmed</SelectItem>
                          <SelectItem value="completed" className="text-primary">Completed</SelectItem>
                          <SelectItem value="cancelled" className="text-status-error">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center text-sm">
                        <Calendar className="w-4 h-4 mr-2 text-text-muted" />
                        <span className="text-text-secondary">{booking.booking_date} at {booking.time_slot}</span>
                      </div>
                      <div className="flex justify-between items-center pt-2 border-t border-white/10">
                        <span className="text-text-secondary">Price</span>
                        <span className="text-xl font-accent text-primary">£{booking.price.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Calendar Tab */}
          {activeTab === 'calendar' && (
            <div data-testid="calendar-section">
              <div className="flex items-center justify-between mb-6">
                <Button variant="outline" onClick={() => {
                  if (calendarMonth === 1) {
                    setCalendarMonth(12);
                    setCalendarYear(calendarYear - 1);
                  } else {
                    setCalendarMonth(calendarMonth - 1);
                  }
                }} className="border-primary/50 text-white hover:bg-primary/10">
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <h2 className="text-2xl font-heading font-bold text-white">
                  {monthNames[calendarMonth - 1]} {calendarYear}
                </h2>
                <Button variant="outline" onClick={() => {
                  if (calendarMonth === 12) {
                    setCalendarMonth(1);
                    setCalendarYear(calendarYear + 1);
                  } else {
                    setCalendarMonth(calendarMonth + 1);
                  }
                }} className="border-primary/50 text-white hover:bg-primary/10">
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>

              {/* Day headers */}
              <div className="grid grid-cols-7 gap-2 mb-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="text-center text-sm font-medium text-text-muted py-2">{day}</div>
                ))}
              </div>

              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-2">
                {renderCalendar()}
              </div>

              {/* Legend */}
              <div className="flex gap-4 mt-6 justify-center">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-status-warning/50"></div>
                  <span className="text-sm text-text-muted">Pending</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-status-success/50"></div>
                  <span className="text-sm text-text-muted">Confirmed</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-primary/50"></div>
                  <span className="text-sm text-text-muted">Completed</span>
                </div>
              </div>
            </div>
          )}

          {/* Messages Tab */}
          {activeTab === 'messages' && (
            <div className="space-y-4" data-testid="messages-section">
              {messages.length === 0 ? (
                <div className="text-center py-12 bg-background-paper rounded-2xl border border-primary/20">
                  <Mail className="w-16 h-16 text-text-muted mx-auto mb-4" />
                  <p className="text-text-secondary font-body">No messages yet</p>
                  <p className="text-text-muted text-sm mt-2">Customer inquiries from the Contact page will appear here</p>
                </div>
              ) : (
                messages.map(msg => (
                  <div key={msg.message_id} className={`bg-background-paper rounded-2xl p-6 border ${msg.status === 'new' ? 'border-primary/50' : 'border-primary/20'}`} data-testid={`message-${msg.message_id}`}>
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-xl ${msg.status === 'new' ? 'bg-primary/20' : 'bg-white/5'}`}>
                          <Mail className={`w-6 h-6 ${msg.status === 'new' ? 'text-primary' : 'text-text-muted'}`} />
                        </div>
                        <div>
                          <h3 className="text-lg font-heading font-semibold text-white">{msg.name}</h3>
                          <a href={`mailto:${msg.email}`} className="text-sm text-primary hover:text-primary-hover">{msg.email}</a>
                          <p className="text-xs text-text-muted mt-1">{new Date(msg.created_at).toLocaleString()}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Select value={msg.status} onValueChange={(value) => handleUpdateMessageStatus(msg.message_id, value)}>
                          <SelectTrigger className={`w-28 rounded-full text-sm ${
                            msg.status === 'new' ? 'bg-primary/20 text-primary border-primary/50' :
                            msg.status === 'read' ? 'bg-blue-500/20 text-blue-400 border-blue-400/50' :
                            msg.status === 'replied' ? 'bg-status-success/20 text-status-success border-status-success/50' :
                            'bg-white/10 text-text-muted border-white/20'
                          }`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-background-paper border-primary/20">
                            <SelectItem value="new" className="text-primary">New</SelectItem>
                            <SelectItem value="read" className="text-blue-400">Read</SelectItem>
                            <SelectItem value="replied" className="text-status-success">Replied</SelectItem>
                            <SelectItem value="archived" className="text-text-muted">Archived</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button size="sm" variant="outline" onClick={() => handleDeleteMessage(msg.message_id)} className="border-status-error/50 text-status-error hover:bg-status-error/10" data-testid={`delete-message-${msg.message_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="bg-background-elevated rounded-xl p-4">
                      <p className="text-text-secondary whitespace-pre-wrap">{msg.message}</p>
                    </div>
                    <div className="flex justify-end mt-4">
                      <a href={`mailto:${msg.email}?subject=Re: Your inquiry to Cakery Wonderland`} className="inline-flex items-center gap-2 text-sm text-primary hover:text-primary-hover">
                        <Reply className="w-4 h-4" />
                        Reply via Email
                      </a>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Reviews Tab */}
          {activeTab === 'reviews' && (
            <div className="space-y-4" data-testid="reviews-section">
              {reviews.length === 0 ? (
                <div className="text-center py-12 bg-background-paper rounded-2xl border border-primary/20">
                  <MessageSquare className="w-16 h-16 text-text-muted mx-auto mb-4" />
                  <p className="text-text-secondary font-body">No reviews yet</p>
                </div>
              ) : (
                reviews.map(review => (
                  <div key={review.review_id} className="bg-background-paper rounded-2xl p-6 border border-primary/20" data-testid={`review-${review.review_id}`}>
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-white font-medium">{review.user_name}</span>
                          <div className="flex">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} className={`w-4 h-4 ${i < review.rating ? 'fill-primary text-primary' : 'text-text-muted'}`} />
                            ))}
                          </div>
                        </div>
                        <p className="text-sm text-text-muted">
                          Product ID: {review.product_id} • {new Date(review.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => handleDeleteReview(review.review_id)} className="border-status-error/50 text-status-error hover:bg-status-error/10" data-testid={`delete-review-${review.review_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-text-secondary">{review.comment}</p>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <div className="space-y-4" data-testid="users-section">
              {users.length === 0 ? (
                <div className="text-center py-12 bg-background-paper rounded-2xl border border-primary/20">
                  <Users className="w-16 h-16 text-text-muted mx-auto mb-4" />
                  <p className="text-text-secondary font-body">No users yet</p>
                </div>
              ) : (
                users.map(u => (
                  <div key={u.user_id} className="bg-background-paper rounded-2xl p-6 border border-primary/20" data-testid={`user-${u.user_id}`}>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-4">
                        {u.picture ? (
                          <img src={u.picture} alt={u.name} className="w-12 h-12 rounded-full border border-primary/30" />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center border border-primary/30">
                            <Users className="w-6 h-6 text-primary" />
                          </div>
                        )}
                        <div>
                          <h3 className="text-lg font-heading font-semibold text-white">{u.name}</h3>
                          <p className="text-sm text-text-muted">{u.email}</p>
                          <p className="text-xs text-text-muted mt-1">
                            {u.auth_type === 'google' ? 'Google Account' : 'Email/Password'} • Joined {new Date(u.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${u.is_admin ? 'bg-primary/20 text-primary' : 'bg-white/10 text-text-muted'}`}>
                          {u.is_admin ? 'Admin' : 'Customer'}
                        </span>
                        {u.user_id !== user.user_id && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleToggleAdmin(u.user_id, u.is_admin)}
                            className={u.is_admin ? 'border-status-error/50 text-status-error hover:bg-status-error/10' : 'border-primary/50 text-primary hover:bg-primary/10'}
                            data-testid={`toggle-admin-${u.user_id}`}
                          >
                            {u.is_admin ? 'Remove Admin' : 'Make Admin'}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AdminDashboard;
